/**
 * DataShieldContext - Program-Aware Data Shielding
 *
 * Manages the reveal session lifecycle and provides field-level shielding
 * decisions based on the effective privacy policy (most restrictive program wins).
 *
 * Key responsibilities:
 * - Track whether the current user has an active reveal session
 * - Auto-expire reveal sessions based on the effective policy timeout
 * - Provide shouldShieldField() for per-field shielding decisions
 * - Store/retrieve reveal tokens in sessionStorage (tab-scoped)
 */

import React, {
  createContext,
  useContext,
  useState,
  useEffect,
  useCallback,
  useRef,
  ReactNode,
} from 'react';
import {
  privacyAPI,
  EffectivePolicy,
  SensitivityTier,
} from '@/services/api/privacy';

// ============================================================================
// TYPES
// ============================================================================

interface RevealSession {
  token: string;
  expiresAt: Date;
  timeoutMinutes: number;
  memberId: number | null; // null = page-level reveal
}

interface DataShieldContextValue {
  // Shield state
  isShieldEnabled: boolean;
  isRevealed: boolean;
  revealExpiresAt: Date | null;
  remainingSeconds: number;

  // Effective policy for the current context
  effectivePolicy: EffectivePolicy | null;

  // Actions
  requestReveal: () => void; // opens the ReauthModal
  revokeReveal: () => void;
  setShowReauthModal: (show: boolean) => void;
  showReauthModal: boolean;

  // Reveal session management (called by ReauthModal on success)
  activateRevealSession: (token: string, expiresAt: string, timeoutMinutes: number, memberId?: number | null) => void;

  // Policy loading
  loadEffectivePolicy: (cboId: number, memberId?: number) => Promise<void>;
  loadOrgPolicy: (cboId: number) => Promise<void>;

  // Field-level checks using backend _shielded_fields metadata
  isFieldShielded: (fieldName: string, shieldedFields?: string[]) => boolean;
}

const DataShieldContext = createContext<DataShieldContextValue | undefined>(undefined);

// ============================================================================
// PROVIDER
// ============================================================================

export function DataShieldProvider({ children }: { children: ReactNode }) {
  const [effectivePolicy, setEffectivePolicy] = useState<EffectivePolicy | null>(null);
  const [revealSession, setRevealSession] = useState<RevealSession | null>(null);
  const [remainingSeconds, setRemainingSeconds] = useState(0);
  const [showReauthModal, setShowReauthModal] = useState(false);
  const timerRef = useRef<ReturnType<typeof setInterval> | null>(null);

  // Derived state
  const isShieldEnabled = effectivePolicy?.data_shield_enabled ?? false;
  const isRevealed = revealSession !== null && new Date() < revealSession.expiresAt;
  const revealExpiresAt = revealSession?.expiresAt ?? null;

  // -----------------------------------------------------------------------
  // RESTORE SESSION FROM sessionStorage
  // -----------------------------------------------------------------------

  useEffect(() => {
    const stored = sessionStorage.getItem('reveal_token');
    const storedExpiry = sessionStorage.getItem('reveal_expires_at');
    const storedTimeout = sessionStorage.getItem('reveal_timeout_minutes');

    if (stored && storedExpiry) {
      const expiresAt = new Date(storedExpiry);
      if (expiresAt > new Date()) {
        setRevealSession({
          token: stored,
          expiresAt,
          timeoutMinutes: parseInt(storedTimeout || '15', 10),
          memberId: null,
        });
      } else {
        // Expired — clean up
        sessionStorage.removeItem('reveal_token');
        sessionStorage.removeItem('reveal_expires_at');
        sessionStorage.removeItem('reveal_timeout_minutes');
      }
    }
  }, []);

  // -----------------------------------------------------------------------
  // COUNTDOWN TIMER
  // -----------------------------------------------------------------------

  useEffect(() => {
    if (timerRef.current) {
      clearInterval(timerRef.current);
      timerRef.current = null;
    }

    if (!revealSession) {
      setRemainingSeconds(0);
      return;
    }

    const updateRemaining = () => {
      const now = new Date();
      const diff = Math.max(0, Math.floor((revealSession.expiresAt.getTime() - now.getTime()) / 1000));
      setRemainingSeconds(diff);

      if (diff <= 0) {
        // Session expired
        revokeReveal();
      }
    };

    updateRemaining();
    timerRef.current = setInterval(updateRemaining, 1000);

    return () => {
      if (timerRef.current) {
        clearInterval(timerRef.current);
      }
    };
  }, [revealSession]);

  // -----------------------------------------------------------------------
  // ACTIONS
  // -----------------------------------------------------------------------

  const requestReveal = useCallback(() => {
    setShowReauthModal(true);
  }, []);

  const revokeReveal = useCallback(() => {
    setRevealSession(null);
    setRemainingSeconds(0);
    sessionStorage.removeItem('reveal_token');
    sessionStorage.removeItem('reveal_expires_at');
    sessionStorage.removeItem('reveal_timeout_minutes');

    // Clear any cached member data from localStorage (offline cache may contain unmasked PII)
    try {
      const keysToRemove: string[] = [];
      for (let i = 0; i < localStorage.length; i++) {
        const key = localStorage.key(i);
        if (key?.startsWith('weavein_api_cache_') && key.includes('/members')) {
          keysToRemove.push(key);
        }
      }
      keysToRemove.forEach((key) => localStorage.removeItem(key));
    } catch (err) {
      console.error('Failed to clear member cache from localStorage:', err);
    }
  }, []);

  const activateRevealSession = useCallback(
    (token: string, expiresAt: string, timeoutMinutes: number, memberId: number | null = null) => {
      const expiry = new Date(expiresAt);
      setRevealSession({
        token,
        expiresAt: expiry,
        timeoutMinutes,
        memberId,
      });

      // Store in sessionStorage (tab-scoped, not persisted across tabs)
      sessionStorage.setItem('reveal_token', token);
      sessionStorage.setItem('reveal_expires_at', expiresAt);
      sessionStorage.setItem('reveal_timeout_minutes', String(timeoutMinutes));

      setShowReauthModal(false);
    },
    []
  );

  // -----------------------------------------------------------------------
  // POLICY LOADING
  // -----------------------------------------------------------------------

  const loadEffectivePolicy = useCallback(async (cboId: number, memberId?: number) => {
    try {
      if (memberId) {
        const policy = await privacyAPI.getMemberEffectivePrivacy(cboId, memberId);
        setEffectivePolicy(policy);
      } else {
        const orgPolicy = await privacyAPI.getOrgPrivacyPolicy(cboId);
        setEffectivePolicy({
          data_shield_enabled: orgPolicy.data_shield_enabled,
          shield_tier_threshold: orgPolicy.shield_tier_threshold,
          reauth_timeout_minutes: orgPolicy.reauth_timeout_minutes,
          require_access_justification: orgPolicy.require_access_justification,
          enhanced_audit_enabled: orgPolicy.enhanced_audit_enabled,
          break_glass_enabled: orgPolicy.break_glass_enabled,
          external_key_exchange_enabled: orgPolicy.external_key_exchange_enabled,
          per_org_encryption_enabled: orgPolicy.per_org_encryption_enabled,
          resolved_from: { org_policy: true, program_ids: [], most_restrictive_program_id: null },
        });
      }
    } catch (err) {
      console.error('Failed to load effective privacy policy:', err);
    }
  }, []);

  const loadOrgPolicy = useCallback(async (cboId: number) => {
    return loadEffectivePolicy(cboId);
  }, [loadEffectivePolicy]);

  // -----------------------------------------------------------------------
  // FIELD-LEVEL CHECKS
  // -----------------------------------------------------------------------

  const isFieldShielded = useCallback(
    (fieldName: string, shieldedFields?: string[]): boolean => {
      // If backend provides _shielded_fields metadata, use it directly
      if (shieldedFields) {
        return shieldedFields.includes(fieldName);
      }
      // Fallback: if shield is enabled and not revealed, assume sensitive fields are shielded
      return isShieldEnabled && !isRevealed;
    },
    [isShieldEnabled, isRevealed]
  );

  // -----------------------------------------------------------------------
  // CONTEXT VALUE
  // -----------------------------------------------------------------------

  const value: DataShieldContextValue = {
    isShieldEnabled,
    isRevealed,
    revealExpiresAt,
    remainingSeconds,
    effectivePolicy,
    requestReveal,
    revokeReveal,
    setShowReauthModal,
    showReauthModal,
    activateRevealSession,
    loadEffectivePolicy,
    loadOrgPolicy,
    isFieldShielded,
  };

  return (
    <DataShieldContext.Provider value={value}>
      {children}
    </DataShieldContext.Provider>
  );
}

// ============================================================================
// HOOKS
// ============================================================================

export function useDataShield(): DataShieldContextValue {
  const context = useContext(DataShieldContext);
  if (!context) {
    throw new Error('useDataShield must be used within a DataShieldProvider');
  }
  return context;
}

/**
 * Convenience hook: returns true if shield is active and data is NOT revealed.
 */
export function useIsShielded(): boolean {
  const { isShieldEnabled, isRevealed } = useDataShield();
  return isShieldEnabled && !isRevealed;
}

/**
 * Format remaining reveal time as "MM:SS".
 */
export function useRevealCountdown(): string {
  const { remainingSeconds } = useDataShield();
  const minutes = Math.floor(remainingSeconds / 60);
  const seconds = remainingSeconds % 60;
  return `${minutes}:${seconds.toString().padStart(2, '0')}`;
}
